import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchAdminTenanciesPage,
  fetchAdminTenancyById,
  fetchAdminTenancyStatusSummary,
} from "../../api/adminData/tenancies";
import { fetchAdminUnitsPage } from "../../api/adminData/units";
import { getUnitHeadingLabel } from "../../utils/unitDisplayId";

import TenantOverviewToolbar from "./tenants/TenantOverviewToolbar";
import { buildTenancyRow } from "./tenants/tenantOverviewData";
import TenancyStatsCards from "./tenancies/TenancyStatsCards";
import TenancyTable from "./tenancies/TenancyTable";
import TenancyDetailPanel from "./tenancies/TenancyDetailPanel";
import TenancyDetailDrawer from "./tenancies/TenancyDetailDrawer";
import { useIsCompactViewport } from "./bexio/useIsCompactViewport";

/**
 * Mietverhältnisse — the central contract list.
 *
 * The page is an orchestrator: it owns the query state, issues the requests and
 * hands plain data to the presentation components under `./tenancies/`.
 *
 * WHY THIS EXISTS BESIDE `/admin/tenants`. Both read the same endpoint, and
 * that is deliberate for now: `/admin/tenants` groups tenancies by unit to
 * answer "what is going on in this flat", while this page lists them flat to
 * answer "show me the contracts" — reference number, lifecycle dates and the
 * money, one row each. `/admin/tenants` is untouched by this slice; whether it
 * later becomes a list of PEOPLE is a separate decision.
 *
 * THE COUNTS DO NOT COME FROM THE ROWS. `GET /api/admin/tenancies/summary` is
 * org-wide and counts by derived display status, so the cards keep saying the
 * same thing as the badges beneath them and stay correct on page 4 of 6.
 *
 * EVERY FILTER IS SERVER-SIDE. Search, status and unit are query parameters,
 * and any change to them starts again at the first page. Filtering the rows
 * already on screen would silently mean "filter within the current page".
 */

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 25;
/** Endpoint maximum; the filter's option list is capped by it, not the data. */
const UNIT_OPTION_LIMIT = 200;

const PENDING = { loading: true, error: null, value: null };

function kpiOk(value) {
  return { loading: false, error: null, value };
}

function kpiFailed(error) {
  return { loading: false, error: error || true, value: null };
}

export default function AdminTenanciesPage() {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [unitId, setUnitId] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  const [rows, setRows] = useState({
    loading: true,
    error: null,
    items: [],
    total: 0,
  });
  const [unitOptions, setUnitOptions] = useState([]);
  const [statusSummary, setStatusSummary] = useState(PENDING);
  // ONE SELECTION, HELD AS AN ID. The row highlight and the detail panel read
  // the same value, so they cannot disagree about what is open.
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState({
    loading: false,
    error: null,
    tenancy: null,
  });
  // Below `lg` the column is hidden and the sheet takes over. The hook reads
  // the same 1023px boundary the column's `hidden lg:block` does — two numbers
  // for one boundary is how a tablet ends up seeing neither.
  const isCompact = useIsCompactViewport();

  // Guards a late response from a superseded query overwriting a newer one.
  const requestSeq = useRef(0);
  // Same job for the detail request: the id whose response is still wanted.
  const detailReq = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Any change to what is being asked for starts again at the first page.
  useEffect(() => {
    setPage(0);
  }, [search, status, unitId, pageSize]);

  const loadRows = useCallback(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setRows((prev) => ({ ...prev, loading: true, error: null }));

    return fetchAdminTenanciesPage({
      search: search || undefined,
      status: status || undefined,
      unit_id: unitId || undefined,
      // Cancelled tenancies are part of "alle beendeten" and the status filter
      // can still narrow to them; hiding them by default would make the list
      // disagree with the card that counts them.
      include_cancelled: true,
      limit: pageSize,
      skip: page * pageSize,
    })
      .then((res) => {
        if (requestSeq.current !== seq) return;
        setRows({
          loading: false,
          error: null,
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total || 0),
        });
      })
      .catch((e) => {
        if (requestSeq.current !== seq) return;
        setRows({
          loading: false,
          error: e?.message || "Mietverhältnisse konnten nicht geladen werden.",
          items: [],
          total: 0,
        });
      });
  }, [search, status, unitId, page, pageSize]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const loadKpis = useCallback(() => {
    setStatusSummary(PENDING);
    fetchAdminTenancyStatusSummary()
      .then((s) => setStatusSummary(kpiOk(s)))
      .catch((e) => setStatusSummary(kpiFailed(e)));
  }, []);

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  useEffect(() => {
    let cancelled = false;
    fetchAdminUnitsPage({ limit: UNIT_OPTION_LIMIT })
      .then((res) => {
        if (cancelled) return;
        const opts = (res?.items || [])
          .map((u) => ({ id: String(u.id), label: getUnitHeadingLabel(u) }))
          .sort((a, b) => a.label.localeCompare(b.label, "de-CH"));
        setUnitOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setUnitOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // `buildTenancyRow` is the shared viewmodel `/admin/tenants` already uses, so
  // both surfaces resolve participants, the canonical move-out and the revenue
  // equivalent by the same rules. `roomsById` is only consulted for the room
  // area, which this table does not show — hence null, and no rooms request.
  // The deposit is added here rather than inside the shared helper: it is on
  // the wire already and nothing else needs it yet.
  const tableRows = useMemo(
    () =>
      rows.items.map((t) => ({
        ...buildTenancyRow(t, null),
        depositAmount: t?.deposit_amount == null ? null : Number(t.deposit_amount),
      })),
    [rows.items]
  );

  // ONE detail request, for the SELECTED tenancy only. The table renders
  // entirely from the list response, so opening a row is the only thing that
  // ever adds a request — never the rendering of the rows themselves.
  //
  // Switching from row A to row B keeps the panel open and swaps its contents:
  // `loading` is raised without clearing `tenancy`, so the panel does not blink
  // through an empty frame. A superseded response is dropped by the id guard,
  // which matters because clicking down a list is faster than the network.
  const loadDetail = useCallback((id) => {
    detailReq.current = id;
    if (!id) {
      setDetail({ loading: false, error: null, tenancy: null });
      return;
    }
    setDetail((prev) => ({ ...prev, loading: true, error: null }));
    fetchAdminTenancyById(id)
      .then((data) => {
        if (detailReq.current !== id) return;
        setDetail({ loading: false, error: null, tenancy: data });
      })
      .catch((e) => {
        if (detailReq.current !== id) return;
        setDetail({
          loading: false,
          error: e?.message || "Mietverhältnis konnte nicht geladen werden.",
          tenancy: null,
        });
      });
  }, []);

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const closeDetail = useCallback(() => setSelectedId(null), []);

  const buildActions = useCallback(
    (row) => [
      {
        key: "details",
        label: "Details anzeigen",
        onSelect: () => setSelectedId(row.id),
      },
      row.tenantId && {
        key: "open-tenant",
        label: "Mieter öffnen",
        onSelect: () =>
          navigate(`/admin/tenants/${encodeURIComponent(row.tenantId)}`),
      },
      row.unitId && {
        key: "open-unit",
        label: "Einheit öffnen",
        onSelect: () =>
          navigate(`/admin/units/${encodeURIComponent(row.unitId)}`),
      },
    ],
    [navigate]
  );

  const totalPages = Math.max(1, Math.ceil((rows.total || 0) / pageSize));
  const paginated = totalPages > 1;
  const filtered = Boolean(search || status || unitId);

  return (
    <div className="mx-auto flex max-w-[1500px] flex-col gap-4 px-6 py-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="m-0 text-[24px] font-semibold leading-tight tracking-[-0.01em] text-foreground">
            Mietverhältnisse
          </h1>
          <p className="m-0 mt-[4px] text-[12.5px] leading-snug text-muted-foreground">
            Alle aktiven, zukünftigen und beendeten Mietverhältnisse verwalten
          </p>
        </div>

        {/* Creating a tenancy needs a tenant, and the only form that exists
            (`TenancyAssignForm`) takes its tenant from the `:tenantId` route
            param of the tenant detail page — it has 61 props and no tenant
            picker of its own. Wiring one up here is its own slice; a second
            copy of that form is the one thing that must not happen. The
            control stays visible so the header reads as it will, and says
            plainly why it cannot act yet. It is drawn as a SECONDARY shape
            rather than a faded primary one: a filled accent button is how a
            page announces its main action, and announcing one that cannot be
            pressed is worse than announcing none. */}
        <button
          type="button"
          disabled
          title="Ein neues Mietverhältnis wird derzeit über den Mieter angelegt: Mieter öffnen → Mietverhältnisse → „Mietverhältnis zuweisen“."
          className="h-[34px] cursor-not-allowed rounded-[7px] border border-border bg-card px-[14px] text-[13px] font-medium text-muted-foreground opacity-70"
        >
          + Neues Mietverhältnis
        </button>
      </header>

      <TenancyStatsCards statusSummary={statusSummary} />

      <TenantOverviewToolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        status={status}
        onStatusChange={setStatus}
        unitId={unitId}
        onUnitChange={setUnitId}
        unitOptions={unitOptions}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      />

      {/* The list and the detail sit side by side from `lg` up; below that the
          column is hidden and the sheet carries the detail instead. `min-w-0`
          on the list column is what lets the table scroll horizontally inside
          it rather than pushing the panel off-screen. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
      {rows.error ? (
        <div
          role="alert"
          className="rounded-[10px] border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-700 dark:text-rose-300"
        >
          <strong className="font-semibold">Fehler beim Laden:</strong>{" "}
          {rows.error}
        </div>
      ) : rows.loading ? (
        <div
          className="flex flex-col gap-3"
          aria-busy="true"
          data-testid="tenancies-loading"
        >
          <span className="sr-only">Mietverhältnisse werden geladen …</span>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              aria-hidden="true"
              className="h-[52px] animate-pulse rounded-[10px] border border-border bg-muted/40"
            />
          ))}
        </div>
      ) : tableRows.length === 0 ? (
        <div
          className="rounded-[10px] border border-dashed border-border bg-muted/30 px-4 py-10 text-center text-[13px] text-muted-foreground"
          data-testid="tenancies-empty"
        >
          {filtered
            ? "Keine Mietverhältnisse für diese Filter gefunden."
            : "Noch keine Mietverhältnisse vorhanden."}
        </div>
      ) : (
        <TenancyTable
          rows={tableRows}
          selectedId={selectedId}
          onSelect={(row) => setSelectedId(row.id)}
          buildActions={buildActions}
        />
      )}

      {!rows.loading && !rows.error && rows.total > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-muted-foreground">
          <span>
            {rows.total}{" "}
            {rows.total === 1 ? "Mietverhältnis" : "Mietverhältnisse"}
            {paginated ? ` · Seite ${page + 1} von ${totalPages}` : ""}
          </span>
          {paginated ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-[7px] border border-border bg-card px-[12px] py-[5px] text-foreground hover:bg-muted disabled:opacity-40"
              >
                Zurück
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded-[7px] border border-border bg-card px-[12px] py-[5px] text-foreground hover:bg-muted disabled:opacity-40"
              >
                Weiter
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
        </div>

        {/* No selection means no column: the list takes the full width rather
            than sitting beside an empty frame.
            `!isCompact` is not a second copy of the column's `hidden lg:block`
            — it is the same boundary, enforced where it now matters. Since
            Slice 3A the detail FETCHES (the task preview, and the documents
            once their tab is open), and a column that is merely CSS-hidden
            below `lg` would still be mounted beside the open sheet and ask
            for all of it twice. */}
        {selectedId && !isCompact ? (
          <TenancyDetailPanel
            tenancy={detail.tenancy}
            loading={detail.loading}
            error={detail.error}
            onRetry={() => loadDetail(selectedId)}
            onClose={closeDetail}
            onCancelled={() => {
              loadDetail(selectedId);
              loadRows();
              loadKpis();
            }}
          />
        ) : null}
      </div>

      {/* Below `lg` the same content tree, in a sheet. `isCompact` reads the
          breakpoint the panel's `hidden lg:block` uses, so exactly one of the
          two is ever showing. */}
      <TenancyDetailDrawer
        open={isCompact && Boolean(selectedId)}
        onOpenChange={(open) => {
          if (!open) closeDetail();
        }}
        tenancy={detail.tenancy}
        loading={detail.loading}
        error={detail.error}
        onRetry={() => loadDetail(selectedId)}
        onCancelled={() => {
          loadDetail(selectedId);
          loadRows();
          loadKpis();
        }}
      />
    </div>
  );
}
